/**
 * Checks if a path is a microsite URL.
 * Pattern: /locations/{state}/{franchise-name}/...
 *
 * @param path - The page path to check
 * @returns True if the path is a microsite URL
 */
export const isMicrositePath = (path: string): boolean => {
    const micrositePattern = /^\/locations\/[^/]+\/[^/]+\//;
    return micrositePattern.test(path);
};

/**
 * Generates a canonical URL for a given path.
 * Ensures consistent formatting and removes trailing slashes, query strings, and hashes.
 * All pages self-canonicalize (microsite pages keep their full path, national pages keep theirs).
 *
 * @param path - The page path/slug (e.g., '/about-us', '/services/water-damage')
 * @param siteUrl - Optional site URL (defaults to production URL)
 * @param prependLocations - If true, prepends /locations to metro/state pages (default: false)
 * @returns The full canonical URL
 */
const getCanonicalUrl = (
    path: string,
    siteUrl = 'https://www.servpro.com',
    prependLocations = false,
): string => {
    // Remove trailing slashes
    const cleanPath = path.replace(/\/+$/, '');

    // Remove query strings and hashes
    const pathWithoutParams = cleanPath.split('?')[0].split('#')[0];

    // All pages self-canonicalize - no stripping of microsite paths
    let pathToUse = pathWithoutParams;

    // Handle metro/state pages that need /locations prefix
    // These come from Contentstack with partial paths like /ga or /san-jose-restoration
    if (prependLocations && !pathToUse.startsWith('/locations')) {
        pathToUse = `/locations${pathToUse}`;
    }

    // Ensure path starts with /
    const normalizedPath = pathToUse.startsWith('/') ? pathToUse : `/${pathToUse}`;

    // Remove trailing slash from siteUrl if present
    const cleanSiteUrl = siteUrl.replace(/\/+$/, '');

    // Handle root path
    if (normalizedPath === '/' || normalizedPath === '') {
        return cleanSiteUrl;
    }

    return `${cleanSiteUrl}${normalizedPath}`;
};

export default getCanonicalUrl;
